<template>
  <div>
    <page-header
      :title="$t('supplier_profile.title')"
      :subtitle="$t('supplier_profile.subtitle')"
      icon="mdi-card-account-details-outline"
      icon-color="primary"
    />

    <div class="px-6 mt-4">
      <v-progress-linear v-if="loading" indeterminate />

      <template v-else-if="form">
        <v-card class="ordera-box-shadow pa-4 mb-6">
          <div class="subtitle-1 font-weight-medium mb-2">{{ $t("supplier_profile.contact_title") }}</div>
          <v-form v-model="valid" @submit.prevent="saveProfile">
            <v-row>
              <v-col cols="12" md="6">
                <text-field v-model="form.name" :label="$t('supplier_profile.name')" :rules="[rules.required]" />
              </v-col>
              <v-col cols="12" md="6">
                <text-field v-model="form.contact" :label="$t('supplier_profile.contact')" />
              </v-col>
              <v-col cols="12" md="6">
                <text-field v-model="form.email" type="email" :label="$t('supplier_profile.email')" />
              </v-col>
              <v-col cols="12" md="6">
                <text-field v-model="form.phone" :label="$t('supplier_profile.phone')" />
              </v-col>
              <v-col cols="12" md="8">
                <text-field v-model="form.address" :label="$t('supplier_profile.address')" />
              </v-col>
              <v-col cols="12" md="4">
                <text-field v-model="form.city" :label="$t('supplier_profile.city')" />
              </v-col>
              <v-col cols="12" md="6">
                <text-field v-model="form.jib" :label="$t('supplier_profile.jib')" />
              </v-col>
              <v-col cols="12" md="6">
                <text-field v-model="form.pib" :label="$t('supplier_profile.pib')" />
              </v-col>
              <v-col cols="12">
                <text-area v-model="form.note" :label="$t('supplier_profile.note')" rows="2" />
              </v-col>
            </v-row>
            <div class="d-flex justify-end">
              <primary-button :disabled="!valid || savingProfile" @click="saveProfile">{{ $t("save") }}</primary-button>
            </div>
          </v-form>
        </v-card>

        <v-card class="ordera-box-shadow pa-4 mb-6">
          <div class="subtitle-1 font-weight-medium">{{ $t("supplier_profile.categories_title") }}</div>
          <div class="body-2 grey--text text--darken-1 mb-2">{{ $t("supplier_profile.categories_hint") }}</div>
          <div v-if="!tree.length" class="grey--text py-4">{{ $t("supplier_profile.no_categories") }}</div>
          <v-treeview
            v-else
            v-model="selected"
            :items="tree"
            item-key="id"
            item-text="name"
            selectable
            selection-type="independent"
            open-on-click
            dense
          />
          <div class="d-flex justify-end mt-2">
            <primary-button :disabled="savingCategories || !tree.length" @click="saveCategories">{{ $t("save") }}</primary-button>
          </div>
        </v-card>
      </template>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/components/global/PageHeader.vue";
import hasValidation from "@/mixins/hasValidation";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";
import productCategoryService from "@/services/productCategory";

const FIELDS = ["name", "email", "phone", "address", "city", "jib", "pib", "contact", "note"];

/** Portal dobavljača: moji podaci (kontakt) i kategorije u kojima imam proizvode. */
export default {
  name: "SupplierProfile",
  mixins: [hasValidation, hasSnackbar],
  components: { PageHeader },
  data: () => ({
    loading: false,
    valid: false,
    form: null,
    tree: [],
    selected: [],
    savingProfile: false,
    savingCategories: false,
  }),
  computed: {
    supplierId() {
      return this.$store.state.user?.user?.suppliers?.[0]?.id;
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      if (!this.supplierId) return;
      this.loading = true;
      try {
        const [supplier, tree, cats] = await Promise.all([
          warehouseService.getSupplier(this.supplierId),
          productCategoryService.getTree(true),
          productCategoryService.getSupplierCategories(this.supplierId),
        ]);
        this.form = Object.fromEntries(FIELDS.map((f) => [f, supplier.data[f] || ""]));
        this.tree = tree;
        this.selected = cats.data.category_ids || [];
      } catch (e) {
        this.showErrorSnackbar(this.$t("error"));
      } finally {
        this.loading = false;
      }
    },
    async saveProfile() {
      this.savingProfile = true;
      try {
        const payload = Object.fromEntries(FIELDS.map((f) => [f, this.form[f] === "" && f !== "name" ? null : this.form[f]]));
        await warehouseService.updateSupplier(this.supplierId, payload);
        this.showSuccessSnackbar(this.$t("supplier_profile.saved"));
      } catch (e) {
        this.showErrorSnackbar(this.$t("error"));
      } finally {
        this.savingProfile = false;
      }
    },
    async saveCategories() {
      this.savingCategories = true;
      try {
        await productCategoryService.setSupplierCategories(this.supplierId, this.selected);
        this.showSuccessSnackbar(this.$t("supplier_profile.categories_saved"));
      } catch (e) {
        this.showErrorSnackbar(this.$t("error"));
      } finally {
        this.savingCategories = false;
      }
    },
  },
};
</script>
